'use client'

import * as React from 'react'
import { cn } from '../../helpers/utils.js'
import { cva, type VariantProps } from 'class-variance-authority'
import useAnchorPosition from './useAnchorPosition.js'
import { Container } from '../../components/container.js'
import { withLayout } from '../../shared/Styles.js'

export interface AnchorNavigationSemanticName {
  root: 'root'
  content: 'content'
  item: 'item'
}

/**
 * 导航项对齐方向样式变体
 */
const anchorNavigationVariants = cva(
  'anchor-navigation-content flex items-center overflow-x-auto [&::-webkit-scrollbar]:hidden',
  {
    variants: {
      alignment: {
        start: 'tablet:justify-start',
        center: 'tablet:justify-center',
        end: 'tablet:justify-end',
      },
      size: {
        small: 'gap-3',
        large: 'gap-6',
      },
    },
    defaultVariants: {
      alignment: 'start',
      size: 'small',
    },
  }
)

/**
 * 导航项样式变体（不含颜色，颜色由 buttonStyle 控制）
 */
const anchorItemVariants = cva(
  'anchor-navigation-item relative shrink-0 text-sm font-bold after:absolute after:bottom-0 after:left-0 after:h-1 after:w-0 after:opacity-0 after:transition-all after:duration-300',
  {
    variants: {
      size: {
        small: 'py-3',
        large: 'py-4',
      },
    },
    defaultVariants: {
      size: 'small',
    },
  }
)

/**
 * 容器主题样式变体
 */
const containerVariants = cva('!sticky top-0 !z-40 w-full', {
  variants: {
    theme: {
      light: 'bg-white',
      dark: 'bg-[#1E2024]',
    },
  },
  defaultVariants: {
    theme: 'light',
  },
})

/**
 * 按钮颜色配置，允许按 theme 分别覆盖默认色、激活文字色、激活指示器色
 */
export interface AnchorButtonStyle {
  /** 默认（未激活）文字色，Tailwind class，如 'text-info-secondary' */
  defaultColor?: string
  /** 激活文字色，Tailwind class，如 'text-info-primary' */
  activeColor?: string
  /** 激活指示器（下划线）背景色，Tailwind class，如 'after:bg-brand-0' */
  activeIndicatorColor?: string
}

/**
 * 锚点项接口
 */
export interface AnchorSectionItem {
  targetId: string
  label: string
}

/**
 * AnchorNavigation 业务组件数据接口
 */
export interface AnchorNavigationData {
  /** 锚点列表 */
  sectionIds: AnchorSectionItem[]
  /** 导航项水平对齐方式 */
  alignment?: 'start' | 'center' | 'end'
  /** 主题模式 */
  theme?: 'light' | 'dark'
  /** 导航大小 */
  size?: 'small' | 'large'
}

export interface AnchorNavigationProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof anchorNavigationVariants> {
  /** 业务数据 */
  data: AnchorNavigationData
  /** 语义化类名 */
  classNames?: Partial<Record<keyof AnchorNavigationSemanticName, string>>
  /** 自定义点击事件，传入时会替代默认的滚动行为 */
  onItemClick?: (item: AnchorSectionItem, index: number) => void
  /**
   * 按钮颜色配置，覆盖 light/dark 主题下的默认颜色。
   * 未传时按主题使用内置默认值。
   *
   * @example
   * // 自定义颜色
   * buttonStyle={{ defaultColor: 'text-info-secondary', activeColor: 'text-info-primary', activeIndicatorColor: 'after:bg-brand-3' }}
   */
  buttonStyle?: AnchorButtonStyle
}

/** light 主题默认颜色 */
const LIGHT_DEFAULTS: Required<AnchorButtonStyle> = {
  defaultColor: 'text-[#4A4C56]',
  activeColor: 'text-[#080A0F]',
  activeIndicatorColor: 'after:bg-brand-0',
}

/** dark 主题默认颜色 */
const DARK_DEFAULTS: Required<AnchorButtonStyle> = {
  defaultColor: 'text-[#8A8D92]',
  activeColor: 'text-white',
  activeIndicatorColor: 'after:bg-brand-0',
}

/**
 * AnchorNavigation - 锚点导航
 *
 * @description 锚点导航
 */
const AnchorNavigation = React.forwardRef<HTMLDivElement, AnchorNavigationProps>(
  ({ classNames = {}, data, onItemClick, buttonStyle, className, ...rest }, ref) => {
    const { alignment = 'start', theme = 'light', size = 'small' } = data
    const activeId = useAnchorPosition(data.sectionIds?.map(item => item.targetId) || [])
    const rootRef = React.useRef<HTMLDivElement>(null)
    const containerRef = React.useRef<HTMLDivElement>(null)
    const sectionRefs = React.useRef<HTMLButtonElement[]>([])

    // 暴露 rootRef 给外部 ref
    React.useImperativeHandle(ref, () => rootRef.current as HTMLDivElement)

    // 合并主题默认值与外部传入的 buttonStyle
    const themeDefaults = theme === 'dark' ? DARK_DEFAULTS : LIGHT_DEFAULTS
    const resolvedButtonStyle: Required<AnchorButtonStyle> = {
      defaultColor: buttonStyle?.defaultColor ?? themeDefaults.defaultColor,
      activeColor: buttonStyle?.activeColor ?? themeDefaults.activeColor,
      activeIndicatorColor: buttonStyle?.activeIndicatorColor ?? themeDefaults.activeIndicatorColor,
    }

    const autoScrollToActiveItem = React.useCallback((activeIdIndex: number) => {
      const curRef = sectionRefs.current[activeIdIndex]

      if (curRef && containerRef.current) {
        const container = containerRef.current
        const button = curRef
        const scrollLeft = button.offsetLeft - container.offsetWidth / 2 + button.offsetWidth / 2

        // 检查 scrollTo 方法是否存在（兼容测试环境和旧浏览器）
        if (typeof container.scrollTo === 'function') {
          container.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          })
        }
      }
    }, [])

    // 当屏幕滚动导致 activeId 变化时，自动滚动导航栏到对应项
    React.useEffect(() => {
      if (!activeId) return

      const activeIndex = data.sectionIds?.findIndex(item => item.targetId === activeId)
      if (activeIndex !== undefined && activeIndex !== -1) {
        autoScrollToActiveItem(activeIndex)
      }
    }, [activeId, data.sectionIds, autoScrollToActiveItem])

    return (
      <Container ref={rootRef} className={cn(containerVariants({ theme }), classNames?.root)} {...rest}>
        <div
          ref={containerRef}
          className={cn(anchorNavigationVariants({ alignment, size }), 'relative', classNames?.content)}
        >
          {data.sectionIds?.map((item, index) => {
            const isActive = activeId === item.targetId

            return (
              <button
                key={item.targetId}
                ref={el => {
                  if (el) {
                    sectionRefs.current[index] = el
                  }
                }}
                aria-current={isActive ? true : undefined}
                onClick={() => {
                  // 默认行为：滚动到对应锚点
                  autoScrollToActiveItem(index)

                  // 如果有自定义点击事件，使用自定义事件
                  if (onItemClick) {
                    onItemClick(item, index)
                    return
                  }

                  // 手动计算滚动位置，考虑导航栏高度
                  const targetElement = document.getElementById(item.targetId)
                  if (targetElement && rootRef.current) {
                    const navHeight = rootRef.current.offsetHeight
                    const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - navHeight

                    window.scrollTo({
                      top: targetPosition,
                      behavior: 'smooth',
                    })
                  }
                }}
                className={cn(
                  anchorItemVariants({ size }),
                  isActive
                    ? [
                        resolvedButtonStyle.activeColor,
                        resolvedButtonStyle.activeIndicatorColor,
                        'after:w-full after:opacity-100',
                      ]
                    : resolvedButtonStyle.defaultColor,
                  classNames?.item
                )}
              >
                {item.label}
              </button>
            )
          })}
        </div>
      </Container>
    )
  }
)

AnchorNavigation.displayName = 'AnchorNavigation'
export default withLayout(AnchorNavigation)
